/**
 * Leveranciersovereenkomsten: lijst of kalender, met statustabs Geldig / Concept /
 * Geannuleerd / Alle (Airtable's Status-formule, gegroepeerd). "Nieuw contract" maakt een
 * concept in Airtable; controleren en versturen gaan daar verder (automations).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { bevestig } from '../../utils/bevestig';
import { STATUS_TABS, contractenOpDag, datumNl, filterOpStatus, maandRaster } from './hrRekenen';
import { ACCENT, INK, haalJson } from './hrUi';

const MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
const LEEG = { leverancierId: '', templateId: '', duurType: 'Bepaalde duur', start: '', eind: '', vergoedingBerekening: 'Dagtarief van Leverancier', bedrag: '', vergoedingType: 'Dag', factuurfrequentie: '', contracttype: '', project: '' };

function NieuwContract({ open, onClose, data, onKlaar }) {
  const [v, setV] = useState(LEEG);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);
  useEffect(() => { if (open) { setV(LEEG); setFout(null); } }, [open]);
  const zet = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));
  const k = data?.keuzes || {};
  const lev = (data?.leveranciers || []).find((l) => l.id === v.leverancierId);

  const opslaan = async () => {
    setFout(null);
    if (!(await bevestig({
      titel: 'Contract aanmaken in Airtable?',
      tekst: `Er komt een concept-overeenkomst voor ${lev?.naam || 'deze leverancier'} in Airtable. Er gaat nog niets naar de leverancier: controleren en versturen doe je daarna in Airtable.`,
      knop: 'Aanmaken',
    }))) return;
    setBezig(true);
    try {
      await haalJson(`${BACKEND_URL}/Api/leveranciers/overeenkomsten`, 'Contract aanmaken', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(v),
      });
      toast.success('Concept-overeenkomst aangemaakt in Airtable');
      onKlaar();
    } catch (e) {
      setFout(e.message);
    } finally {
      setBezig(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nieuw contract</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {fout && <Alert severity="error">{fout}</Alert>}
        <TextField select label="Leverancier" value={v.leverancierId} onChange={zet('leverancierId')} required>
          {(data?.leveranciers || []).map((l) => <MenuItem key={l.id} value={l.id}>{l.naam}{l.categorie === 'Bedrijf' ? ' (bedrijf)' : ''}</MenuItem>)}
        </TextField>
        <TextField select label="Template" value={v.templateId} onChange={zet('templateId')} required
          helperText={(data?.templates || []).find((t) => t.id === v.templateId)?.omschrijving?.slice(0, 160)}>
          {(data?.templates || []).map((t) => <MenuItem key={t.id} value={t.id}>{t.naam}</MenuItem>)}
        </TextField>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
          <TextField select label="Duur" value={v.duurType} onChange={zet('duurType')}>
            {(k.duurType || []).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
          </TextField>
          <TextField type="date" label="Startdatum" value={v.start} onChange={zet('start')} InputLabelProps={{ shrink: true }} required />
          {v.duurType === 'Bepaalde duur' && (
            <TextField type="date" label="Einddatum" value={v.eind} onChange={zet('eind')} InputLabelProps={{ shrink: true }} required />
          )}
        </Box>
        <TextField select label="Vergoeding" value={v.vergoedingBerekening} onChange={zet('vergoedingBerekening')}>
          {(k.vergoedingBerekening || []).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
        </TextField>
        {v.vergoedingBerekening === 'Handmatig' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField type="number" label="Bedrag (€)" value={v.bedrag} onChange={zet('bedrag')} />
            <TextField select label="Per" value={v.vergoedingType} onChange={zet('vergoedingType')}>
              {(k.vergoedingType || []).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </TextField>
          </Box>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField select label="Factuurfrequentie" value={v.factuurfrequentie} onChange={zet('factuurfrequentie')}>
            <MenuItem value="">—</MenuItem>
            {(k.factuurfrequentie || []).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
          </TextField>
          <TextField select label="Contracttype" value={v.contracttype} onChange={zet('contracttype')}>
            <MenuItem value="">Volgt uit de leverancier</MenuItem>
            {(k.contracttype || []).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
          </TextField>
        </Box>
        <TextField label="Project (vrije tekst)" value={v.project} onChange={zet('project')} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" onClick={opslaan} disabled={bezig || !v.leverancierId || !v.templateId || !v.start}
          sx={{ textTransform: 'none', bgcolor: ACCENT.color }}>
          {bezig ? 'Bezig…' : 'Concept aanmaken'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Kalender({ items }) {
  const nu = new Date();
  const [m, setM] = useState({ jaar: nu.getFullYear(), maand: nu.getMonth() });
  const weken = useMemo(() => maandRaster(m.jaar, m.maand), [m]);
  const vandaag = new Date().toISOString().slice(0, 10);
  const schuif = (n) => setM(({ jaar, maand }) => {
    const d = new Date(jaar, maand + n, 1);
    return { jaar: d.getFullYear(), maand: d.getMonth() };
  });
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton size="small" onClick={() => schuif(-1)} aria-label="Vorige maand"><ChevronLeftIcon /></IconButton>
        <Typography sx={{ fontWeight: 700, minWidth: 150, textAlign: 'center' }}>{MAANDEN[m.maand]} {m.jaar}</Typography>
        <IconButton size="small" onClick={() => schuif(1)} aria-label="Volgende maand"><ChevronRightIcon /></IconButton>
        <Typography sx={{ fontSize: 12, color: INK.stil, ml: 2 }}>▶ gaat in · ■ loopt af</Typography>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(110px, 1fr))', border: `1px solid ${INK.lijn}`, borderRadius: 2, overflow: 'hidden', minWidth: 770 }}>
          {['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'].map((d) => (
            <Box key={d} sx={{ px: 1, py: 0.5, fontSize: 12, fontWeight: 700, color: INK.zacht, bgcolor: INK.vlak, borderBottom: `1px solid ${INK.lijn}` }}>{d}</Box>
          ))}
          {weken.flat().map((dag) => {
            const opDag = contractenOpDag(items, dag.datum);
            return (
              <Box key={dag.datum} sx={{ minHeight: 92, p: 0.75, borderTop: `1px solid ${INK.lijn}`, borderLeft: `1px solid ${INK.lijn}`, bgcolor: dag.buiten ? INK.vlak : '#fff' }}>
                <Typography sx={{ fontSize: 12, color: dag.datum === vandaag ? ACCENT.color : INK.stil, fontWeight: dag.datum === vandaag ? 800 : 500 }}>{dag.dag}</Typography>
                {opDag.slice(0, 3).map((o) => (
                  <Tooltip key={`${o.id}-${o.soortDag}`} title={`${o.leverancier} — ${o.status}${o.periode ? ` (${o.periode})` : ''}`}>
                    <Typography noWrap sx={{ fontSize: 12, color: INK.tekst }}>{o.soortDag === 'start' ? '▶' : '■'} {o.leverancier}</Typography>
                  </Tooltip>
                ))}
                {opDag.length > 3 && <Typography sx={{ fontSize: 11, color: INK.stil }}>+{opDag.length - 3}</Typography>}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

export default function Overeenkomsten({ magBewerken }) {
  const [d, setD] = useState(null);
  const [fout, setFout] = useState(null);
  const [tab, setTab] = useState('geldig');
  const [weergave, setWeergave] = useState('lijst');
  const [nieuw, setNieuw] = useState(false);

  const laad = useCallback(async () => {
    setFout(null);
    try { setD(await haalJson(`${BACKEND_URL}/Api/leveranciers/overeenkomsten`, 'Overeenkomsten laden')); }
    catch (e) { setFout(e.message); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  const items = useMemo(() => filterOpStatus(d?.overeenkomsten, tab), [d, tab]);
  const tel = (key) => filterOpStatus(d?.overeenkomsten, key).length;

  if (fout) return <Alert severity="error" action={<Button color="inherit" size="small" onClick={laad}>Opnieuw</Button>}>{fout}</Alert>;
  if (!d) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  const nieuwKnop = magBewerken && (
    <Tooltip title={d.schrijvenAan ? '' : 'Schrijven naar Airtable staat uit op test'}>
      <span>
        <Button variant="outlined" disabled={!d.schrijvenAan} onClick={() => setNieuw(true)} sx={{ textTransform: 'none' }}>Nieuw contract</Button>
      </span>
    </Tooltip>
  );

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ minHeight: 36, '& .MuiTab-root': { textTransform: 'none', minHeight: 36, fontWeight: 600 } }}>
          {STATUS_TABS.map((t) => <Tab key={t.key} value={t.key} label={`${t.label} (${tel(t.key)})`} />)}
        </Tabs>
        <ToggleButtonGroup size="small" exclusive value={weergave} onChange={(_e, v) => v && setWeergave(v)} sx={{ ml: 'auto' }}>
          <ToggleButton value="lijst" sx={{ textTransform: 'none' }}>Lijst</ToggleButton>
          <ToggleButton value="kalender" sx={{ textTransform: 'none' }}>Kalender</ToggleButton>
        </ToggleButtonGroup>
        {nieuwKnop}
      </Box>
      {!d.schrijvenAan && magBewerken && (
        <Typography sx={{ fontSize: 12, color: INK.stil, mb: 1.5 }}>Schrijven naar Airtable staat uit op test — nieuwe contracten maak je in productie.</Typography>
      )}

      {weergave === 'kalender' ? <Kalender items={items} /> : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& th': { color: INK.zacht, fontWeight: 700, fontSize: 12 }, '& td': { fontSize: 13, color: INK.tekst } }}>
            <TableHead>
              <TableRow>
                <TableCell>Leverancier</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Periode</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Vergoeding</TableCell>
                <TableCell>Document</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((o) => (
                <TableRow key={o.id}>
                  <TableCell sx={{ fontWeight: 600 }}>{o.leverancier}</TableCell>
                  <TableCell>{o.status}</TableCell>
                  <TableCell>{o.start ? `${datumNl(o.start)}${o.eind ? ` t/m ${datumNl(o.eind)}` : ' (onbepaald)'}` : '—'}</TableCell>
                  <TableCell>{o.template || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>{o.project || '—'}</TableCell>
                  <TableCell>{o.vergoeding || '—'}</TableCell>
                  <TableCell>{o.documentBeschikbaar ? 'Ondertekend' : '—'}</TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={7} sx={{ color: INK.zacht }}>Geen overeenkomsten in deze tab.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Box>
      )}
      <NieuwContract open={nieuw} onClose={() => setNieuw(false)} data={d} onKlaar={() => { setNieuw(false); laad(); }} />
    </>
  );
}
